import { sectionRenderer } from '@theme/section-renderer';

const SECTION_ID = 'search-drawer-popular';
const VISITOR_KEY = 'search-drawer-visitor';
const PICKED_KEY_PREFIX = 'search-drawer-popular-picked:';
const DISPLAY_COUNT = 3;
const POOL_MIN = 3;

let popularFetchController = null;

/**
 * Stable visitor id — each browser gets a consistent identity.
 * @returns {string}
 */
function getVisitorId() {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return `session-${Date.now()}`;
  }
}

/**
 * Seeded shuffle so each visitor gets a stable but unique product set.
 * @param {number[]} pool
 * @param {string} seed
 * @returns {number[]}
 */
function shuffleWithSeed(pool, seed) {
  const items = [...pool];
  let state = 0;

  for (let i = 0; i < seed.length; i++) {
    state = (state + seed.charCodeAt(i) * (i + 1)) % 2147483647;
  }

  for (let i = items.length - 1; i > 0; i--) {
    state = (state * 48271) % 2147483647;
    const j = state % (i + 1);
    [items[i], items[j]] = [items[j], items[i]];
  }

  return items;
}

/**
 * Pick product ids for this visitor from the curated pool.
 * @param {number[]} pool
 * @returns {number[]}
 */
function pickProductIdsForVisitor(pool) {
  if (!pool.length) return [];

  const visitorId = getVisitorId();
  const cacheKey = `${PICKED_KEY_PREFIX}${visitorId}`;

  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length) {
        const valid = parsed.filter((id) => pool.includes(Number(id)));
        if (valid.length >= Math.min(DISPLAY_COUNT, pool.length)) {
          return valid.slice(0, DISPLAY_COUNT).map(Number);
        }
      }
    }
  } catch {
    /* use fresh pick */
  }

  const shuffled = shuffleWithSeed(pool, visitorId);
  const picked = shuffled.slice(0, Math.min(DISPLAY_COUNT, shuffled.length));

  try {
    localStorage.setItem(cacheKey, JSON.stringify(picked));
  } catch {
    /* ignore */
  }

  return picked;
}

/**
 * @param {ParentNode} root
 */
function initVariantSelects(root) {
  root.querySelectorAll('.search-drawer-variant-select').forEach((select) => {
    if (select instanceof HTMLSelectElement) {
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

/**
 * Load popular search products for the current visitor.
 */
export async function loadSearchDrawerPopular() {
  const wrapper = document.querySelector('[data-search-popular]');
  const poolEl = document.getElementById('search-popular-pool-data');
  const mount = document.querySelector('[data-search-popular-list]');

  if (!wrapper || !poolEl || !mount) return;

  let pool = [];

  try {
    pool = JSON.parse(poolEl.textContent || '[]');
  } catch {
    pool = [];
  }

  pool = pool.map(Number).filter((id) => Number.isFinite(id) && id > 0);

  if (pool.length < POOL_MIN) {
    mount.innerHTML = '';
    return;
  }

  const pickedIds = pickProductIdsForVisitor(pool);
  if (!pickedIds.length) return;

  if (popularFetchController) {
    popularFetchController.abort();
  }
  popularFetchController = new AbortController();

  wrapper.classList.add('is-loading');

  try {
    const searchRoot = window.Shopify?.routes?.root || '/';
    const url = new URL(`${searchRoot}search`, window.location.origin);
    url.searchParams.set('q', pickedIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    const sectionHTML = await sectionRenderer.getSectionHTML(SECTION_ID, false, url);

    if (popularFetchController.signal.aborted) return;

    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const list = doc.querySelector('[data-search-popular-list]');

    mount.innerHTML = list ? list.innerHTML : '';
    initVariantSelects(mount);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    console.error('Search drawer popular load error:', error);
  } finally {
    wrapper.classList.remove('is-loading');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  loadSearchDrawerPopular();
});
